import { ChipModal, ChipModalBody, ChipModalField, ChipModalFooter, ChipModalHeader, ChipSelect } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router";
import {
	type Asset,
	emptyDocument,
	fileUrl,
	readContent,
	type StudioFile,
	studioKey,
	studioOptions,
} from "~/common/studio/api";
import { type ContentFlowData, flowFinished, flowItemIds, flowPollInterval } from "~/common/studio/flow";
import { useAccess } from "~/core/access";
import { ApiError, post } from "~/core/api";
import { pick, useI18n, useT } from "~/core/i18n";
import presets from "../../../../packages/content/media-presets.json";

export type Modality = "image" | "speech" | "video";

const MODALITIES: Modality[] = ["image", "speech", "video"];
/** The lengths a clip is offered at. The gateway caps a request at 60s. */
const SECONDS = [5, 8, 10, 15];
const NAMES: Record<Modality, [string, string]> = {
	image: ["Image", "图片"],
	speech: ["Narration", "配音"],
	video: ["Video", "视频"],
};
const BRIEFS: Record<Modality, [string, string]> = {
	image: ["Image description", "图片描述"],
	speech: ["Narration text", "配音文本"],
	video: ["Video description", "视频描述"],
};
const ROLES: Record<Modality, string> = { image: "image", speech: "audio", video: "video" };
const PREFIX: Record<Modality, string> = { image: "image/", speech: "audio/", video: "video/" };

/** Which generators an upload control's accepted types leave room for. */
export function generatable(accept: string | undefined): Modality[] {
	const kinds: [string, Modality][] = [
		["image/", "image"],
		["video/", "video"],
		["audio/", "speech"],
	];
	return kinds.filter(([mime]) => accept?.includes(mime)).map(([, modality]) => modality);
}

export interface GeneratorProps {
	brandId: string;
	/** Names the work a generated asset is saved under. */
	title: string;
	/**
	 * The work to generate into. Without one the generator saves an asset-only
	 * work of its own, which is what a caller that only wants the file does.
	 */
	target?: { id: string; revision: number; blocked?: boolean };
	modalities?: Modality[];
	disabled?: boolean;
	/** Offered when the caller can take the file — the composer attaching it. */
	onUse?: (fileId: string, alt: string, contentType: string) => void;
	/** A generation finished; a caller holding the same work refetches it. */
	onDone?: () => void;
}

type Generator = ReturnType<typeof useGenerator>;

function useGenerator({ brandId, title, target, modalities, disabled, onUse, onDone }: GeneratorProps, open: boolean) {
	const { locale } = useI18n();
	const t = useT();
	const { editor } = useAccess();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const kinds = modalities?.length ? modalities : MODALITIES;
	const [modality, setModality] = useState<Modality>(kinds[0]);
	const [prompt, setPrompt] = useState("");
	const [model, setModel] = useState("");
	const [preset, setPreset] = useState("landscape");
	const [seconds, setSeconds] = useState(SECONDS[0]);
	const [workflowId, setWorkflowId] = useState("");
	const requestId = useRef(crypto.randomUUID());
	const submittedArgs = useRef<unknown>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const submitting = useRef(false);
	const options = useQuery({
		queryKey: [...studioKey(brandId), "options"],
		queryFn: () => studioOptions(brandId),
		enabled: open,
	});
	const models = options.data?.models.filter((m) => m.modality === modality) ?? [];
	const selected = models.find((m) => m.id === model);
	const flow = useQuery({
		queryKey: ["content-flow", brandId, workflowId],
		queryFn: () =>
			import("~/core/api").then(({ request }) =>
				request<ContentFlowData>(`/agent/content/${brandId}/workflows/${workflowId}`),
			),
		enabled: open && !!workflowId,
		refetchInterval: (q) => flowPollInterval(q.state.data),
	});
	const savedItemId = flow.data ? flowItemIds(flow.data)[0] : "";
	const result = useQuery({
		queryKey: [...studioKey(brandId), savedItemId, "generation", workflowId, flowFinished(flow.data)],
		queryFn: () => readContent(brandId, savedItemId),
		enabled: open && !!savedItemId,
		refetchInterval: (q) =>
			!flowFinished(flow.data) || q.state.data?.renders?.some((r) => ["queued", "running"].includes(r.status))
				? 3000
				: false,
	});
	const jobId = flow.data?.operations.find((op) => op.block_id === "generate")?.job_id;
	const render = result.data?.renders?.find((r) => r.id === jobId);
	const produced: Asset | undefined = render?.output?.files?.find((a) => a.role === ROLES[modality]);
	const file: StudioFile | undefined = result.data?.files.find(
		(f) => f.id === produced?.file_id && f.content_type.startsWith(PREFIX[modality]),
	);
	const locked = disabled || !editor || busy || !!target?.blocked;
	const submitted = !!workflowId;

	async function generate() {
		if (locked || submitted || submitting.current || !selected || !prompt.trim()) return;
		submitting.current = true;
		setBusy(true);
		setError("");
		try {
			if (!submittedArgs.current) {
				const existing = target ? await readContent(brandId, target.id) : null;
				submittedArgs.current = {
					request_id: requestId.current,
					content: existing
						? {
								id: existing.id,
								expected_revision: target!.revision,
								title: existing.title,
								kind: existing.kind,
								locale: existing.locale,
								document: existing.document,
							}
						: {
								title: title.slice(0, 200) || tr("Media asset", "媒体素材"),
								kind: "gallery",
								asset_only: true,
								locale,
								document: emptyDocument(),
							},
					generate: { modality, model, prompt: prompt.trim(), preset, ...(modality === "video" ? { seconds } : {}) },
				};
			}
			const created = await post<{ workflow_id: string }>(`/agent/content/${brandId}/workflows`, submittedArgs.current);
			setWorkflowId(created.workflow_id);
			onDone?.();
		} catch (e) {
			setError(
				e instanceof ApiError
					? e.localized(locale)
					: tr(
							"Submission could not be confirmed. Retry checks the same workflow request without creating a second one.",
							"暂时无法确认提交结果，再次提交会检查同一次流程请求，不会重复创建。",
						),
			);
		} finally {
			setBusy(false);
			submitting.current = false;
		}
	}

	function reset() {
		setWorkflowId("");
		requestId.current = crypto.randomUUID();
		submittedArgs.current = null;
		setError("");
	}

	const done = !!file;
	const finished = flowFinished(flow.data) && (!savedItemId || result.isSuccess);
	const primary = {
		label: done
			? onUse
				? tr("Use asset", "使用素材")
				: tr("Generate another", "再生成一个")
			: finished
				? tr("Start a new generation", "开始新的生成")
				: submitted
					? tr("Generation submitted", "已提交生成")
					: tr("Run generation workflow", "运行生成流程"),
		disabled: locked || (!done && !finished && (submitted || !selected || !prompt.trim())),
		onClick: () => {
			if (locked) return;
			if (finished && !done) return reset();
			if (!done) return void generate();
			if (onUse && file) onUse(file.id, produced?.alt ?? prompt, file.content_type);
			reset();
		},
	};
	return {
		t,
		tr,
		locale,
		kinds,
		modality,
		setModality,
		prompt,
		setPrompt,
		model,
		setModel,
		preset,
		setPreset,
		seconds,
		setSeconds,
		options,
		models,
		selected,
		locked,
		submitted,
		error,
		result,
		render,
		file,
		produced,
		itemId: savedItemId,
		workflowId,
		flow,
		brandId,
		primary,
	};
}

function Fields({ g }: { g: Generator }) {
	const { tr, locale } = g;
	return (
		<fieldset className="bare-fieldset stack" disabled={g.locked || g.submitted || !!g.error}>
			{g.kinds.length > 1 && (
				<ChipModalField type="custom" title={tr("Asset type", "素材类型")}>
					<ChipSelect
						aria-label={tr("Asset type", "素材类型")}
						value={g.modality}
						onChange={(value) => {
							g.setModality(value as Modality);
							g.setModel("");
						}}
						options={g.kinds.map((m) => ({ value: m, label: pick(locale, NAMES[m]) }))}
					/>
				</ChipModalField>
			)}
			<ChipModalField
				type="textarea"
				title={pick(locale, BRIEFS[g.modality])}
				value={g.prompt}
				onChange={g.setPrompt}
				maxLength={4000}
				rows={4}
				placeholder={
					g.modality === "speech"
						? tr("Enter the exact narration.", "填写配音原文。")
						: tr("Describe what to generate.", "描述需要生成的内容。")
				}
			/>
			<ChipModalField type="custom" title={tr("Model", "模型")}>
				<ChipSelect
					searchable
					searchPlaceholder={tr("Search models", "搜索模型")}
					aria-label={tr("Model", "模型")}
					value={g.model}
					onChange={g.setModel}
					disabled={g.locked || g.submitted || g.options.isPending}
					placeholder={tr("Choose a model", "选择模型")}
					options={g.models.map((m) => ({ value: m.id, label: m.name || m.id }))}
				/>
			</ChipModalField>
			{g.modality !== "speech" && (
				<ChipModalField type="custom" title={tr("Aspect ratio", "画面比例")}>
					<ChipSelect
						aria-label={tr("Aspect ratio", "画面比例")}
						value={g.preset}
						onChange={g.setPreset}
						disabled={g.locked || g.submitted}
						options={presets.presets.map((p) => ({
							value: p.id,
							label: `${pick(locale, p.label)} · ${aspectRatio(p.width, p.height)}`,
						}))}
					/>
				</ChipModalField>
			)}
			{g.modality === "video" && (
				<ChipModalField type="custom" title={tr("Length", "时长")}>
					<ChipSelect
						aria-label={tr("Length", "时长")}
						value={String(g.seconds)}
						onChange={(value) => g.setSeconds(Number(value))}
						disabled={g.locked || g.submitted}
						options={SECONDS.map((s) => ({ value: String(s), label: tr(`${s} seconds`, `${s} 秒`) }))}
					/>
				</ChipModalField>
			)}
		</fieldset>
	);
}

function Status({ g }: { g: Generator }) {
	const { tr, t } = g;
	const rate = g.selected && {
		en: `Uses credits at this model's rate: ${g.selected.credits_per_1000_input.toFixed(2)} / ${g.selected.credits_per_1000_output.toFixed(2)} per 1k input/output ${g.modality === "speech" ? "characters" : g.modality === "video" ? "seconds" : "units"}.`,
		zh: `按模型实际用量扣费：每千输入 / 输出${g.modality === "speech" ? "字符" : g.modality === "video" ? "秒" : "单位"} ${g.selected.credits_per_1000_input.toFixed(2)} / ${g.selected.credits_per_1000_output.toFixed(2)} 积分。`,
	};
	return (
		<>
			{rate && <p className="hint">{tr(rate.en, rate.zh)}</p>}
			{g.options.isPending && <p role="status">{tr("Loading models…", "正在加载模型…")}</p>}
			{!g.options.isPending && !g.options.error && !g.models.length && (
				<p role="status">{tr("No model of this type is available right now.", "当前没有这一类型的可用模型。")}</p>
			)}
			{g.options.error && (
				<p role="alert">{tr("Could not load models. Reopen to retry.", "模型加载失败，请重新打开重试。")}</p>
			)}
			{g.error && <p role="alert">{g.error}</p>}
			{g.result.error && (
				<p role="alert">
					{tr("Could not refresh generation status. Reopen to check again.", "生成状态刷新失败，请重新打开查看。")}
				</p>
			)}
			{g.submitted && !g.file && !g.error && !flowFinished(g.flow.data) && (
				<p role="status">
					{g.render ? t(`tasks.${g.render.status}`) : tr("Waiting for generation…", "正在等待生成…")}
					{g.render?.error && ` · ${g.render.error}`}
				</p>
			)}
			{g.file && <Preview file={g.file} alt={g.produced?.alt ?? g.prompt} />}
			{flowFinished(g.flow.data) && !g.file && (
				<p role="alert">
					{tr(
						"Generation stopped. Open the workflow to inspect completed steps and results.",
						"生成已停止，请打开流程查看已完成的步骤与结果。",
					)}
				</p>
			)}
			{g.flow.error && (
				<p role="alert">
					{tr("Could not refresh workflow progress. Open the workflow to check.", "流程进度刷新失败，请打开流程查看。")}
				</p>
			)}
			{g.workflowId && (
				<Link target="_blank" rel="noopener" to={`/w/${g.brandId}/agent/w/${g.workflowId}`}>
					{tr("Open generation workflow", "打开生成流程")}
				</Link>
			)}
		</>
	);
}

function Preview({ file, alt }: { file: StudioFile; alt: string }) {
	const src = fileUrl(file.id);
	if (file.content_type.startsWith("video/"))
		// biome-ignore lint/a11y/useMediaCaption: a generated clip has no track to offer yet.
		return <video className="w-full" src={src} controls />;
	if (file.content_type.startsWith("audio/"))
		// biome-ignore lint/a11y/useMediaCaption: narration is the caption's own source text.
		return <audio className="w-full" src={src} controls />;
	return <img className="w-full" src={src} alt={alt} />;
}

/** Generate a saved asset for a screen that only wants the file. */
export function GenerateMediaDialog({
	open,
	onOpenChange,
	...props
}: GeneratorProps & { open: boolean; onOpenChange: (open: boolean) => void }) {
	const g = useGenerator(props, open);
	const heading = g.tr("Generate media", "生成素材");
	return (
		<ChipModal open={open} onOpenChange={onOpenChange} srTitle={heading}>
			<ChipModalHeader onClose={() => onOpenChange(false)} closeAriaLabel={g.t("common.close")}>
				{heading}
			</ChipModalHeader>
			<ChipModalBody>
				<Fields g={g} />
				<ChipModalField type="custom" title="">
					<Status g={g} />
				</ChipModalField>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={g.t("common.close")}
				onCancel={() => onOpenChange(false)}
				defaultAction="none"
				primaryAction={{
					...g.primary,
					onClick: () => {
						const used = !!g.file;
						g.primary.onClick();
						if (used) onOpenChange(false);
					},
				}}
			/>
		</ChipModal>
	);
}

function aspectRatio(width: number, height: number): string {
	let a = width;
	let b = height;
	while (b) [a, b] = [b, a % b];
	return `${width / a}:${height / a}`;
}
